import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { clearSession, fetchMe, getStoredToken } from "@/lib/api";

type AdminRouteGuardProps = {
  children: ReactNode;
};

type GuardStatus = "checking" | "allowed" | "denied";

const AdminRouteGuard = ({ children }: AdminRouteGuardProps) => {
  const location = useLocation();
  const [status, setStatus] = useState<GuardStatus>(() => (getStoredToken() ? "checking" : "denied"));

  useEffect(() => {
    if (!getStoredToken()) {
      setStatus("denied");
      return;
    }

    let cancelled = false;
    setStatus("checking");
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        if (["ADMIN", "ROLE_ADMIN"].includes(me.roleType)) {
          setStatus("allowed");
          return;
        }
        clearSession();
        setStatus("denied");
      })
      .catch(() => {
        if (!cancelled) {
          clearSession();
          setStatus("denied");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-sm text-slate-300" role="status">
        관리자 권한을 확인하고 있습니다.
      </div>
    );
  }

  if (status === "denied") {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  return children;
};

export default AdminRouteGuard;
